import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });

  // Check if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      // Store token in memory-only context (not localStorage)
      login(response.data.token, response.data.username);
      toast.success("Login successful!");
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error("Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [formData, login, navigate]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin Login | TJP Carpentry & Construction</title>
      </Helmet>
      <Header />

      {/* Login Form */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-full mb-6">
              <Lock className="text-white" size={28} />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
              Admin Login
            </h1>
            <p className="text-stone-600">
              Sign in to manage quote requests
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl shadow-stone-200/50 p-8">
            <div className="space-y-6">
              <div>
                <Label htmlFor="username" className="text-slate-900 font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="admin"
                  className="mt-2 h-12"
                  required
                  data-testid="admin-username-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-900 font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="mt-2 h-12"
                  required
                  data-testid="admin-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 h-12 font-semibold"
                data-testid="admin-login-button"
              >
                {loading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </div>
          </form>

          <p className="text-center text-stone-500 text-sm mt-6">
            Default credentials: admin / admin123
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminLoginPage;
